/* Script: eating-guide.js
   Summary: Drives Eating Guide data retrieval, rendering, filtering, and article interactions. */

const DATA_STORAGE_KEY = "eatingGuideRawData:v1";

const scheduleIdle =
  typeof window !== "undefined" && typeof window.requestIdleCallback === "function"
    ? (callback) => window.requestIdleCallback(callback, { timeout: 1500 })
    : (callback) => window.setTimeout(callback, 0);

function readCachedDataset() {
  try {
    return typeof window !== "undefined" && window.sessionStorage
      ? window.sessionStorage.getItem(DATA_STORAGE_KEY)
      : null;
  } catch (error) {
    console.warn("Unable to read cached Eating Guide dataset.", error);
    return null;
  }
}

function writeCachedDataset(rawText) {
  if (!rawText) return;
  try {
    if (typeof window !== "undefined" && window.sessionStorage) {
      window.sessionStorage.setItem(DATA_STORAGE_KEY, rawText);
    }
  } catch (error) {
    console.warn("Unable to cache Eating Guide dataset.", error);
  }
}

const FILTER_STORAGE_KEY = "eatingGuideFilters:v1";

function readStoredFilters() {
  if (typeof window === "undefined") {
    return { guideline: "", type: "" };
  }
  try {
    const raw = window.sessionStorage?.getItem(FILTER_STORAGE_KEY);
    if (!raw) return { guideline: "", type: "" };
    const parsed = JSON.parse(raw);
    return {
      guideline: typeof parsed.guideline === "string" ? parsed.guideline : "",
      type: typeof parsed.type === "string" ? parsed.type : ""
    };
  } catch (error) {
    console.warn("Unable to read cached Eating Guide filters.", error);
    return { guideline: "", type: "" };
  }
}

function persistFilters(guideline, type) {
  if (typeof window === "undefined" || !window.sessionStorage) return;
  try {
    if (!guideline && !type) {
      window.sessionStorage.removeItem(FILTER_STORAGE_KEY);
    } else {
      window.sessionStorage.setItem(
        FILTER_STORAGE_KEY,
        JSON.stringify({ guideline, type })
      );
    }
  } catch (error) {
    console.warn("Unable to cache Eating Guide filters.", error);
  }
}

/* Section: Data Fetching */
let eatingGuideDataPromise = null;

async function fetchArticleData() {
  if (eatingGuideDataPromise) {
    return eatingGuideDataPromise;
  }
  const cachedRawData = readCachedDataset();
  if (cachedRawData) {
    eatingGuideDataPromise = Promise.resolve(cachedRawData);
    return eatingGuideDataPromise;
  }
  eatingGuideDataPromise = (async () => {
    const url = "/pages/all-eating-guide-articles-data";
    try {
      const response = await fetch(url);
      if (!response.ok) {
        console.error(`Failed to fetch data from ${url}. Status: ${response.status}`);
        eatingGuideDataPromise = null;
        return null;
      }
      const text = await response.text();
      writeCachedDataset(text);
      return text;
    } catch (error) {
      console.error(`Error fetching data from ${url}:`, error);
      eatingGuideDataPromise = null;
      return null;
    }
  })();
  return eatingGuideDataPromise;
}

/* Section: JSON Preparation */
function sanitizeJsonString(value) {
  if (!value || typeof value !== 'string') return '';
  let result = '';
  let inString = false;
  let consecutiveBackslashes = 0;

  for (let i = 0; i < value.length; i++) {
    const char = value[i];

    if (char === '\\') {
      consecutiveBackslashes += 1;
      result += char;
      continue;
    }

    if (char === '"') {
      if (consecutiveBackslashes % 2 === 0) {
        inString = !inString;
      }
      consecutiveBackslashes = 0;
      result += char;
      continue;
    }

    if (inString) {
      if (char === '\r') {
        result += '\\n';
        if (value[i + 1] === '\n') {
          i += 1;
        }
        consecutiveBackslashes = 0;
        continue;
      }
      if (char === '\n') {
        result += '\\n';
        consecutiveBackslashes = 0;
        continue;
      }
      if (char === '\t') {
        result += '\\t';
        consecutiveBackslashes = 0;
        continue;
      }
      if (char === '\f') {
        result += '\\f';
        consecutiveBackslashes = 0;
        continue;
      }
      if (char === '\b') {
        result += '\\b';
        consecutiveBackslashes = 0;
        continue;
      }
      if (char === '\v') {
        result += '\\u000b';
        consecutiveBackslashes = 0;
        continue;
      }
      if (char === '\u2028') {
        result += '\\u2028';
        consecutiveBackslashes = 0;
        continue;
      }
      if (char === '\u2029') {
        result += '\\u2029';
        consecutiveBackslashes = 0;
        continue;
      }
    }

    consecutiveBackslashes = 0;
    result += char;
  }

  return result;
}

function prepareRawString(rawString) {
  if (!rawString || typeof rawString !== 'string') return '';
  const trimmed = rawString.trim();
  if (!trimmed) return trimmed;
  // remove any leading/trailing scripts or notes before first [ and after last ]
  const startIndex = trimmed.indexOf('[');
  const endIndex = trimmed.lastIndexOf(']');
  if (startIndex === -1 || endIndex === -1 || endIndex <= startIndex) {
    return sanitizeJsonString(trimmed);
  }
  const sliced = trimmed.slice(startIndex, endIndex + 1);
  return sanitizeJsonString(sliced);
}

function attemptJsonParse(rawString) {
  try {
    return JSON.parse(rawString);
  } catch (error) {
    return null;
  }
}

/* Section: Article Dataset Parsing */
function getArticleData(rawString) {
  if (window.allEatingGuideArticles) {
    return window.allEatingGuideArticles;
  }
  if (!rawString || typeof rawString !== 'string') {
    console.error("Eating Guide Error: rawString is not available.");
    return [];
  }
  const prepared = prepareRawString(rawString);
  let articles = attemptJsonParse(prepared);
  if (Array.isArray(articles) && articles.length) {
    window.allEatingGuideArticles = articles;
    return articles;
  }
  console.warn("Falling back to tolerant JSON parser for article data.");
  const cleanedString = rawString.trim().replace(/^[^\[]*/, '').replace(/[^]]*$/, '');
  const rawEntries = cleanedString.slice(1, -1).split('},{');
  const parsedEntries = [];
  let failedEntries = 0;
  rawEntries.forEach((entry, index) => {
    const normalizedEntry = sanitizeJsonString((index === 0 ? entry : '{' + entry) + (index === rawEntries.length - 1 ? '' : '}'));
    try {
      const parsed = JSON.parse(normalizedEntry);
      if (parsed) {
        parsedEntries.push(parsed);
      }
    } catch (error) {
      failedEntries += 1;
      if (failedEntries <= 3) {
        console.error(`Failed to recover entry ${index}.`, error);
      }
    }
  });
  if (failedEntries > 3) {
    console.error(`Failed to recover ${failedEntries} article entries due to malformed data.`);
  }
  window.allEatingGuideArticles = parsedEntries;
  return parsedEntries;
}

/* Section: Field Helpers */
function extractFieldValue(field) {
  if (!field) return '';
  if (typeof field === 'string') return field;
  if (typeof field === 'object' && 'value' in field) {
    return typeof field.value === 'string' ? field.value : '';
  }
  return '';
}

function stripHtmlTags(html) {
  if (!html) return '';
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || '';
}

function truncateText(text, maxLength = 220) {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trim()}…`;
}

function sanitizeHandle(value) {
  if (!value && value !== 0) return '';
  let handle = String(value).trim();
  if (!handle) return '';
  handle = handle.split('?')[0];
  handle = handle.replace(/^https?:\/\/[^/]+/i, '');
  handle = handle.replace(/^\/+|\/+$/g, '');
  handle = handle.replace(/^blogs\/eating-guide\//, '');
  return handle.toLowerCase();
}

function escapeHtml(value) {
  if (typeof value !== 'string') return '';
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function normalizeRichTextJson(rawString) {
  if (!rawString || typeof rawString !== 'string') return '';
  let normalized = rawString.trim();
  if (!normalized) return '';
  if (normalized.includes('"=>')) {
    normalized = normalized.replace(/"([^"]+)"=>/g, '"$1":');
  }
  if (normalized.includes('=>')) {
    normalized = normalized.replace(/([{\s])'([^']+)'=>/g, '$1"$2":');
    normalized = normalized.replace(/([{\s])([^"'\s][^:\s]*)=>/g, '$1"$2":');
  }
  return normalized;
}

/* Section: Rich Text Rendering */
function renderRichTextNode(node) {
  if (!node || typeof node !== 'object') return '';
  const type = node.type || 'text';

  switch (type) {
    case 'root':
      return (node.children || []).map(renderRichTextNode).join('');
    case 'paragraph':
      return `<p>${(node.children || []).map(renderRichTextNode).join('')}</p>`;
    case 'text': {
      let content = escapeHtml(node.value || '');
      if (node.bold) content = `<strong>${content}</strong>`;
      if (node.italic) content = `<em>${content}</em>`;
      if (node.underline) content = `<u>${content}</u>`;
      if (node.strikethrough) content = `<s>${content}</s>`;
      if (node.code) content = `<code>${content}</code>`;
      return content;
    }
    case 'list': {
      const tag = node.listType === 'ordered' ? 'ol' : 'ul';
      const items = (node.children || []).map(renderRichTextNode).join('');
      return `<${tag}>${items}</${tag}>`;
    }
    case 'list-item':
      return `<li>${(node.children || []).map(renderRichTextNode).join('')}</li>`;
    case 'heading': {
      const level = Math.min(Math.max(parseInt(node.level, 10) || 2, 1), 6);
      const childrenHtml = (node.children || []).map(renderRichTextNode).join('');
      return `<h${level}>${childrenHtml}</h${level}>`;
    }
    case 'link': {
      const href = escapeHtml(node.url || node.href || '#');
      const title = node.title ? ` title="${escapeHtml(node.title)}"` : '';
      const target = node.target ? ` target="${escapeHtml(node.target)}"` : ' target="_blank"';
      const rel = node.rel ? ` rel="${escapeHtml(node.rel)}"` : ' rel="noopener noreferrer"';
      const children = (node.children || []).map(renderRichTextNode).join('');
      return `<a href="${href}"${title}${target}${rel}>${children}</a>`;
    }
    case 'blockquote':
      return `<blockquote>${(node.children || []).map(renderRichTextNode).join('')}</blockquote>`;
    case 'hard_break':
      return '<br>';
    default:
      return (node.children || []).map(renderRichTextNode).join('');
  }
}

function convertRichTextString(rawString) {
  if (!rawString || typeof rawString !== 'string') return null;
  const trimmed = rawString.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return null;
  }
  const normalized = normalizeRichTextJson(rawString);
  if (!normalized) return null;
  try {
    const parsed = JSON.parse(normalized);
    const html = renderRichTextNode(parsed);
    return html || null;
  } catch (error) {
    return null;
  }
}

function hydrateRichTextBlocks() {
  const blocks = document.querySelectorAll('.eating-guide-rich-text[data-rich-text]');
  blocks.forEach(block => {
    const raw = block.getAttribute('data-rich-text');
    if (!raw) return;
    const html = convertRichTextString(raw);
    if (html) {
      block.innerHTML = html;
      block.classList.add('rich-text-hydrated');
    }
  });
}

/* Section: Card Rendering */
function renderAllCards(articles) {
  const grid = document.getElementById("article-grid");
  if (!grid) {
    console.warn("No article-grid element found.");
    return;
  }

  if (articles.length === 0) {
    grid.innerHTML = '<p>No eating guide articles found. Data may be syncing from the automation.</p>';
    return;
  }

  articles.sort((a, b) => {
    const nameA = (a.name && a.name.value) ? a.name.value.toLowerCase() : "";
    const nameB = (b.name && b.name.value) ? b.name.value.toLowerCase() : "";
    return nameA.localeCompare(nameB);
  });

  const placeholderImg = "https://cdn.shopify.com/s/files/1/0903/1428/files/placeholder.png?v=1747849096";
  const articlesHtml = articles.map((article, index) => {
    const name = article.name && article.name.value ? article.name.value : "";
    const url = `/blogs/eating-guide/${article.handle || ""}`;
    const guideline = article.guideline && article.guideline.value ? article.guideline.value : "";
    const type = article.type && article.type.value ? article.type.value : "";
    const latin_name = article.latin_name && article.latin_name.value ? article.latin_name.value : "";
    const description = (article.description && article.description.value ? article.description.value : "").substring(0, 100) + '... [read more]';
    const image_src = article.image && article.image.url ? article.image.url : placeholderImg;
    const image_alt = article.image && article.image.altText ? article.image.altText : "Placeholder";

    const guideline_handle = guideline.toLowerCase().replace(/ /g, '-');
    const type_handle = type.toLowerCase().replace(/ /g, '-');

    if (index < 2) {
    }

    const cardHtml = `
      <a href="${url}" class="eating-guide-card-link" data-original-url="${url}">
        <div
          class="eating-guide-card guideline-${guideline_handle}${index < 10 ? ' visible' : ''}"
          data-guideline="${guideline_handle}"
          data-type="${type_handle}"
          data-name="${name.replace(/"/g, '&quot;')}"
        >
          <img
            data-src="${image_src}"
            alt="${image_alt.replace(/"/g, '&quot;')}"
            class="lazy-image"
            onerror="this.onerror=null; this.src='${placeholderImg}';"
          >
          <h2>${name}</h2>
          <p class="latin-name">${latin_name}</p>
          <p class="guideline"><b>Guideline:</b> ${guideline}</p>
          <p class="type"><b>Type:</b> ${type}</p>
          <p class="description">${description}</p>
        </div>
      </a>
    `;
    if (index < 2) {
    }
    return cardHtml;
  }).join('');

  grid.innerHTML = articlesHtml;

  document.querySelectorAll('#article-grid .eating-guide-card').forEach((card, index) => {
    const img = card.querySelector('.lazy-image');
    if (index < 10) {
      if (img && img.dataset.src) {
        img.src = img.dataset.src;
        img.removeAttribute("data-src");
        img.classList.add("visible");
        card.classList.add("visible");
      }
    } else {
      if (img) window.eatingGuideObserver?.observe(img);
    }
  });
}

/* Section: Main Guide Page */
async function initializeMainGuidePage() {
  const grid = document.getElementById("article-grid");
  if (!grid) return;

  const rawString = await fetchArticleData();
  const articles = getArticleData(rawString);
  renderAllCards(articles);

  const storedFilters = readStoredFilters();
  const urlParams = new URLSearchParams(window.location.search);

  let activeGuideline = (urlParams.get("guideline") || "").toLowerCase();
  let activeType = (urlParams.get("type") || "").toLowerCase();

  const validGuidelines = ["free-feed", "limited-daily", "sometimes-treats", "never-feed", "poisonous"];
  if (!validGuidelines.includes(activeGuideline)) activeGuideline = "";
  const validTypes = Array.from(document.querySelectorAll(".eating-guide-type-icon")).map(icon => icon.dataset.type.toLowerCase());
  if (!validTypes.includes(activeType)) activeType = "";

  if (!activeGuideline && storedFilters.guideline) {
    activeGuideline = storedFilters.guideline;
  }
  if (!activeType && storedFilters.type) {
    activeType = storedFilters.type;
  }

  persistFilters(activeGuideline, activeType);

  if (urlParams.toString() && window.history && window.history.replaceState) {
    const cleanUrl = `${window.location.pathname}${window.location.hash || ""}`;
    window.history.replaceState({}, "", cleanUrl);
  }

  document.querySelectorAll(".eating-guide-guideline-buttons button").forEach(button => {
    const isActive = button.dataset.guideline.toLowerCase() === activeGuideline;
    button.classList.toggle("active", isActive);
    button.setAttribute("aria-pressed", isActive ? "true" : "false");
  });
  document.querySelectorAll(".eating-guide-type-icon").forEach(icon => {
    const isActive = icon.dataset.type.toLowerCase() === activeType;
    icon.classList.toggle("active", isActive);
    icon.setAttribute("aria-pressed", isActive ? "true" : "false");
  });

  function applyFilters() {
    const cardLinks = grid.querySelectorAll(".eating-guide-card-link");
    const noResultsNotice = document.getElementById("no-results-notice");
    let visibleCount = 0;

    cardLinks.forEach(link => {
      const card = link.querySelector(".eating-guide-card");
      if (!card) return;

      const cardGuideline = card.dataset.guideline || "";
      const cardType = card.dataset.type || "";

      const matchesGuideline = !activeGuideline || cardGuideline === activeGuideline;
      const matchesType = !activeType || cardType === activeType;

      if (matchesGuideline && matchesType) {
        link.style.display = "block";
        card.classList.add("visible");
        visibleCount++;
        if (link.dataset.originalUrl) {
          link.setAttribute("href", link.dataset.originalUrl);
        }
      } else {
        link.style.display = "none";
      }
    });

    if (noResultsNotice) {
      const guidelineText = activeGuideline ? document.querySelector(`.eating-guide-guideline-buttons button[data-guideline="${activeGuideline}"]`)?.textContent.trim() || activeGuideline.replace(/-/g, " ") : "";
      const typeText = activeType ? document.querySelector(`.eating-guide-type-icon[data-type="${activeType}"] img`)?.alt || activeType.replace(/-/g, " ") : "";
      let message = "No items match your current selection.";
      if (guidelineText && typeText) {
        message = `There are no ${typeText.toLowerCase()} items that are also ${guidelineText.toLowerCase()}.`;
      } else if (guidelineText) {
        message = `There are no items that are ${guidelineText.toLowerCase()}.`;
      } else if (typeText) {
        message = `There are no ${typeText.toLowerCase()} items found.`;
      }
      noResultsNotice.querySelector("p").textContent = message;
      noResultsNotice.style.display = (visibleCount === 0 && (activeGuideline || activeType)) ? "block" : "none";
    }
    persistFilters(activeGuideline, activeType);
    if (activeGuideline && activeType) {
      const filterBar = document.querySelector(".eating-guide-filter");
      const typeScrollBar = document.querySelector(".eating-guide-type-scroll");
      let offset = 0; // Adjusted to account for both sticky elements
      if (filterBar && getComputedStyle(filterBar).position === "sticky") {
        offset += filterBar.offsetHeight;
      }
      if (typeScrollBar && getComputedStyle(typeScrollBar).position === "sticky") {
        offset += typeScrollBar.offsetHeight;
      }
      const elementPosition = grid.getBoundingClientRect().top + window.pageYOffset;
      const targetScrollPosition = elementPosition - offset;
      window.scrollTo({
        top: targetScrollPosition < 0 ? 0 : targetScrollPosition,
        behavior: "smooth",
      });
    }
  }

  window.resetFilters = function() {
    activeGuideline = "";
    activeType = "";
    document.querySelectorAll(".eating-guide-guideline-buttons button.active, .eating-guide-type-icon.active").forEach(el => {
      el.classList.remove("active");
      el.setAttribute("aria-pressed", "false");
    });
    persistFilters("", "");
    if (window.history && window.history.replaceState) {
      const cleanUrl = `${window.location.pathname}${window.location.hash || ""}`;
      window.history.replaceState({}, "", cleanUrl);
    }
    applyFilters();
  };

  document.querySelectorAll(".eating-guide-guideline-buttons button").forEach(button => {
    button.addEventListener("click", () => {
      const newGuideline = button.dataset.guideline.toLowerCase();
      activeGuideline = button.classList.contains("active") ? "" : newGuideline;
      document.querySelectorAll(".eating-guide-guideline-buttons button").forEach(btn => {
        const isActive = btn.dataset.guideline.toLowerCase() === activeGuideline;
        btn.classList.toggle("active", isActive);
        btn.setAttribute("aria-pressed", isActive ? "true" : "false");
      });
      applyFilters();
    });
  });

  document.querySelectorAll(".eating-guide-type-icon").forEach(icon => {
    const handler = () => {
      const newType = icon.dataset.type.toLowerCase();
      activeType = icon.classList.contains("active") ? "" : newType;
      document.querySelectorAll(".eating-guide-type-icon").forEach(ico => {
        const isActive = ico.dataset.type.toLowerCase() === activeType;
        ico.classList.toggle("active", isActive);
        ico.setAttribute("aria-pressed", isActive ? "true" : "false");
      });
      applyFilters();
    };
    icon.addEventListener("click", handler);
    icon.addEventListener("keydown", e => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        handler();
      }
    });
  });

  if (activeGuideline || activeType) {
    applyFilters();
  }
}

/* Section: Article Page */
function initializeSingleArticlePage() {
  const mainDiv = document.querySelector(".eating-guide-article .article-content");
  if (!mainDiv) {
    console.warn("No single article content div found.");
    return;
  }

  async function displayRelatedArticles() {
    const relatedContainer = document.getElementById("related-articles-container");
    const noRelatedMessage = document.getElementById("no-related-articles-message");
    if (!relatedContainer || !noRelatedMessage) {
      console.warn("Missing related articles elements.");
      return;
    }

    const rawString = await fetchArticleData();
    const allArticles = getArticleData(rawString);

    const currentGuideline = mainDiv.dataset.guidelineHandleized;
    const currentType = mainDiv.dataset.typeHandleized;
    const currentHandle = sanitizeHandle(mainDiv.dataset.articleHandle);

    const relatedArticles = allArticles.filter(article => {
      const articleHandle = sanitizeHandle(article.handle);
      if (!articleHandle || articleHandle === currentHandle) {
        return false;
      }
      const guidelineHandle = (article.guideline && article.guideline.value ? article.guideline.value : "").toLowerCase().replace(/ /g, '-');
      const typeHandle = (article.type && article.type.value ? article.type.value : "").toLowerCase().replace(/ /g, '-');
      return guidelineHandle === currentGuideline &&
             typeHandle === currentType;
    }).slice(0, 10);

    const safeRelatedArticles = relatedArticles.filter(article => {
      const handle = sanitizeHandle(article.handle);
      return handle && handle !== currentHandle;
    });

    if (safeRelatedArticles.length > 0) {
      relatedContainer.innerHTML = safeRelatedArticles.map((article, index) => {
        const handle = sanitizeHandle(article.handle);
        const url = `/blogs/eating-guide/${handle}`;
        const guidelineHandle = (article.guideline && article.guideline.value ? article.guideline.value : "").toLowerCase().replace(/ /g, '-');
        const name = article.name && article.name.value ? article.name.value : "";
        const latin_name = article.latin_name && article.latin_name.value ? article.latin_name.value : "";
        const image_src = article.image && article.image.url ? article.image.url : "https://cdn.shopify.com/s/files/1/0903/1428/files/placeholder.png?v=1747849096";
        const image_alt = article.image && article.image.altText ? article.image.altText : "Placeholder";
        const cardHtml = `
          <a href="${url}" class="eating-guide-card-link related-card-link">
            <div class="eating-guide-card guideline-${guidelineHandle} related-card visible">
              <img
                data-src="${image_src}"
                alt="${image_alt.replace(/"/g, '&quot;')}"
                class="lazy-image"
                onerror="this.src='https://cdn.shopify.com/s/files/1/0903/1428/files/placeholder.png?v=1747849096';"
              >
              <p class="related-name">${name}</p>
              <p class="related-latin-name">${latin_name}</p>
            </div>
          </a>
        `;
        return cardHtml;
      }).join('');
      noRelatedMessage.style.display = 'none';
      relatedContainer.querySelectorAll('.lazy-image').forEach(img => window.eatingGuideObserver?.observe(img));
    } else {
      noRelatedMessage.style.display = 'block';
    }
  }

  async function setupOftenMistakenItems() {
    const mistakenSection = document.getElementById("mistaken-items-section");
    const mistakenContainer = document.getElementById("mistaken-items-container");
    const noMistakenMessage = document.getElementById("no-mistaken-items-message");
    const detailPanel = document.getElementById("mistaken-detail-panel");
    const detailContent = document.getElementById("mistaken-detail-content");
    const detailArrow = detailPanel ? detailPanel.querySelector(".mistaken-detail-arrow") : null;

    if (!mistakenSection || !mistakenContainer || !detailPanel || !detailContent || !detailArrow) {
      return;
    }

    const rawSimilarHandles = (mainDiv.dataset.similarItems || "").trim();
    if (!rawSimilarHandles) {
      mistakenSection.remove();
      return;
    }

    let similarHandles = [];
    if (rawSimilarHandles.startsWith("[") && rawSimilarHandles.endsWith("]")) {
      try {
        const parsed = JSON.parse(rawSimilarHandles);
        if (Array.isArray(parsed)) {
          similarHandles = parsed.map(handle => String(handle || '').trim()).filter(Boolean);
        }
      } catch (error) {
        console.warn("Failed to parse similar items JSON array:", error);
      }
    }

    if (similarHandles.length === 0) {
      similarHandles = rawSimilarHandles
        .split(/[\n\r\t,;|]+/)
        .map(handle => handle.trim())
        .filter(Boolean);
    }

    if (similarHandles.length === 0) {
      mistakenSection.remove();
      return;
    }

    const rawString = await fetchArticleData();
    const allArticles = getArticleData(rawString);
    if (!Array.isArray(allArticles) || allArticles.length === 0) {
      console.warn("No article data available for similar items.");
      mistakenSection.remove();
      return;
    }

    const articleLookup = new Map();
    allArticles.forEach(article => {
      if (article && article.handle) {
        articleLookup.set(article.handle, article);
      }
    });

    const matchingArticles = similarHandles
      .map(handle => articleLookup.get(handle))
      .filter(Boolean);

    if (matchingArticles.length === 0) {
      mistakenContainer.remove();
      detailPanel.remove();
      if (noMistakenMessage) {
        noMistakenMessage.hidden = false;
      }
      return;
    }

    const placeholderImg = "https://cdn.shopify.com/s/files/1/0903/1428/files/placeholder.png?v=1747849096";
    const activeClass = "is-active";
    const guidelinePrefix = "guideline-";
    const detailPanelGuidelineClasses = new Set(["guideline-free-feed", "guideline-limited-daily", "guideline-sometimes-treats", "guideline-never-feed", "guideline-poisonous"]);
    const articlesByHandle = new Map();
    matchingArticles.forEach(article => {
      articlesByHandle.set(article.handle, article);
    });

    mistakenContainer.innerHTML = "";

    matchingArticles.forEach(article => {
      const handle = article.handle || "";
      const name = extractFieldValue(article.name);
      const latinName = extractFieldValue(article.latin_name);
      const guideline = extractFieldValue(article.guideline);
      const type = extractFieldValue(article.type);
      const guidelineHandle = guideline.toLowerCase().replace(/[^a-z0-9]+/g, "-");
      const imageSrc = (article.image && article.image.url) ? article.image.url : placeholderImg;
      const imageAlt = (article.image && article.image.altText) ? article.image.altText : "Placeholder";

      const triggerButton = document.createElement("button");
      triggerButton.type = "button";
      triggerButton.className = "mistaken-card-trigger";
      triggerButton.dataset.handle = handle;
      triggerButton.dataset.guideline = guidelineHandle;
      triggerButton.setAttribute("aria-expanded", "false");
      triggerButton.setAttribute("aria-controls", "mistaken-detail-panel");

      const innerCard = document.createElement("div");
      innerCard.className = `mistaken-card guideline-${guidelineHandle}`;

      const img = document.createElement("img");
      img.src = imageSrc;
      img.alt = imageAlt;
      innerCard.appendChild(img);

      const nameEl = document.createElement("p");
      nameEl.className = "mistaken-name";
      nameEl.textContent = name;
      innerCard.appendChild(nameEl);

      if (latinName) {
        const latinEl = document.createElement("p");
        latinEl.className = "mistaken-latin-name";
        latinEl.textContent = latinName;
        innerCard.appendChild(latinEl);
      }

      if (guideline) {
        const guidelineEl = document.createElement("p");
        guidelineEl.className = "mistaken-guideline";
        guidelineEl.textContent = guideline;
        innerCard.appendChild(guidelineEl);
      }

      if (type) {
        const typeEl = document.createElement("p");
        typeEl.className = "mistaken-type";
        typeEl.textContent = type;
        innerCard.appendChild(typeEl);
      }

      triggerButton.appendChild(innerCard);
      mistakenContainer.appendChild(triggerButton);
    });

    if (noMistakenMessage) {
      noMistakenMessage.hidden = true;
    }

    let activeHandle = "";

    function resetActiveCard() {
      mistakenContainer.querySelectorAll(`.${activeClass}`).forEach(button => {
        button.classList.remove(activeClass);
        button.setAttribute("aria-expanded", "false");
      });
    }

    function removeGuidelineTint(element) {
      detailPanelGuidelineClasses.forEach(className => {
        element.classList.remove(className);
      });
    }

    function applyDetailContent(article) {
      if (!article) return;
      detailContent.innerHTML = "";

      const name = extractFieldValue(article.name);
      const latinName = extractFieldValue(article.latin_name);
      const guideline = extractFieldValue(article.guideline);
      const type = extractFieldValue(article.type);
      const description = truncateText(stripHtmlTags(extractFieldValue(article.description)), 320);
      const forYourPet = truncateText(stripHtmlTags(extractFieldValue(article.for_your_pet)), 320);
      const warning = truncateText(stripHtmlTags(extractFieldValue(article.warning)), 280);
      const resources = stripHtmlTags(extractFieldValue(article.resources));
      const imageSrc = (article.image && article.image.url) ? article.image.url : "https://cdn.shopify.com/s/files/1/0903/1428/files/placeholder.png?v=1747849096";
      const imageAlt = (article.image && article.image.altText) ? article.image.altText : "Placeholder";

      const topRow = document.createElement("div");
      topRow.className = "mistaken-detail-top";

      const imageWrapper = document.createElement("div");
      imageWrapper.className = "mistaken-detail-image";
      const img = document.createElement("img");
      img.src = imageSrc;
      img.alt = imageAlt;
      imageWrapper.appendChild(img);

      const infoWrapper = document.createElement("div");
      infoWrapper.className = "mistaken-detail-info";

      const nameHeading = document.createElement("h3");
      nameHeading.textContent = name || "Similar Item";
      infoWrapper.appendChild(nameHeading);

      if (latinName) {
        const latinEl = document.createElement("p");
        latinEl.className = "latin-name";
        latinEl.textContent = latinName;
        infoWrapper.appendChild(latinEl);
      }

      if (guideline) {
        const guidelineEl = document.createElement("p");
        guidelineEl.className = "guideline";
        const label = document.createElement("b");
        label.textContent = "Guideline:";
        guidelineEl.appendChild(label);
        guidelineEl.appendChild(document.createTextNode(` ${guideline}`));
        infoWrapper.appendChild(guidelineEl);
      }

      if (type) {
        const typeEl = document.createElement("p");
        typeEl.className = "type";
        const label = document.createElement("b");
        label.textContent = "Type:";
        typeEl.appendChild(label);
        typeEl.appendChild(document.createTextNode(` ${type}`));
        infoWrapper.appendChild(typeEl);
      }

      topRow.appendChild(imageWrapper);
      topRow.appendChild(infoWrapper);
      detailContent.appendChild(topRow);

      if (description) {
        const descEl = document.createElement("p");
        descEl.className = "description";
        descEl.textContent = description;
        detailContent.appendChild(descEl);
      }

      if (forYourPet) {
        const petEl = document.createElement("p");
        petEl.className = "for-your-pet";
        const label = document.createElement("b");
        label.textContent = "For Your Pet:";
        petEl.appendChild(label);
        petEl.appendChild(document.createTextNode(` ${forYourPet}`));
        detailContent.appendChild(petEl);
      }

      if (warning) {
        const warningEl = document.createElement("p");
        warningEl.className = "warning";
        const label = document.createElement("b");
        label.textContent = "Warning:";
        warningEl.appendChild(label);
        warningEl.appendChild(document.createTextNode(` ${warning}`));
        detailContent.appendChild(warningEl);
      }

      if (resources) {
        const resourcesEl = document.createElement("p");
        resourcesEl.className = "resources";
        const label = document.createElement("b");
        label.textContent = "Resources:";
        resourcesEl.appendChild(label);
        resourcesEl.appendChild(document.createTextNode(` ${resources}`));
        detailContent.appendChild(resourcesEl);
      }

      const linkWrapper = document.createElement("div");
      linkWrapper.className = "mistaken-detail-actions";

      const viewMoreLink = document.createElement("a");
      viewMoreLink.className = "eating-guide-pill mistaken-view-more";
      viewMoreLink.href = `/blogs/eating-guide/${article.handle || ""}`;
      viewMoreLink.textContent = "View More";
      linkWrapper.appendChild(viewMoreLink);

      detailContent.appendChild(linkWrapper);
    }

    function positionArrow(trigger) {
      requestAnimationFrame(() => {
        const triggerRect = trigger.getBoundingClientRect();
        const panelRect = detailPanel.getBoundingClientRect();
        const arrowPosition = triggerRect.left + triggerRect.width / 2 - panelRect.left;
        const clampedPosition = Math.max(20, Math.min(arrowPosition, panelRect.width - 20));
        detailArrow.style.left = `${clampedPosition}px`;
      });
    }

    mistakenContainer.addEventListener("click", event => {
      const trigger = event.target.closest(".mistaken-card-trigger");
      if (!trigger) return;
      const handle = trigger.dataset.handle || "";
      if (!handle) return;

      if (activeHandle === handle && !detailPanel.hidden) {
        detailPanel.hidden = true;
        detailPanel.classList.remove("active");
        resetActiveCard();
        activeHandle = "";
        return;
      }

      const article = articlesByHandle.get(handle);
      if (!article) return;

      activeHandle = handle;
      resetActiveCard();
      trigger.classList.add(activeClass);
      trigger.setAttribute("aria-expanded", "true");

      const guidelineHandle = trigger.dataset.guideline || "";
      removeGuidelineTint(detailPanel);
      if (guidelineHandle) {
        detailPanel.classList.add(`${guidelinePrefix}${guidelineHandle}`);
      }

      applyDetailContent(article);
      detailPanel.hidden = false;
      detailPanel.classList.add("active");
      positionArrow(trigger);
    });

    mistakenContainer.addEventListener("keydown", event => {
      if (event.key !== "Enter" && event.key !== " ") return;
      const trigger = event.target.closest(".mistaken-card-trigger");
      if (!trigger) return;
      event.preventDefault();
      trigger.click();
    });
  }

  function setupNextPrevArticleNavigation() {
    const navContainer = document.getElementById("article-navigation-container");
    const prevButton = document.getElementById("prev-article-button");
    const nextButton = document.getElementById("next-article-button");
    if (!navContainer || !prevButton || !nextButton) {
      console.warn("Missing navigation elements.");
      return;
    }

    const currentHandle = sanitizeHandle(mainDiv.dataset.articleHandle);
    const storedFiltersForNav = readStoredFilters();
    const activeGuideline = storedFiltersForNav.guideline || "";
    const activeType = storedFiltersForNav.type || "";

    async function setupNavigation() {
      const rawString = await fetchArticleData();
      const allArticles = getArticleData(rawString);

      allArticles.sort((a, b) => {
        const nameA = (a.name && a.name.value) ? a.name.value.toLowerCase() : "";
        const nameB = (b.name && b.name.value) ? b.name.value.toLowerCase() : "";
        return nameA.localeCompare(nameB);
      });

      let navigableArticles = allArticles;
      if (activeGuideline) {
        navigableArticles = navigableArticles.filter(article => 
          (article.guideline && article.guideline.value ? article.guideline.value.toLowerCase().replace(/ /g, '-') : "") === activeGuideline
        );
      }
      if (activeType) {
        navigableArticles = navigableArticles.filter(article => 
          (article.type && article.type.value ? article.type.value.toLowerCase().replace(/ /g, '-') : "") === activeType
        );
      }

      const currentIndex = navigableArticles.findIndex(article => sanitizeHandle(article.handle) === currentHandle);
      if (currentIndex === -1) {
        navContainer.style.display = 'none';
        return;
      }


      if (currentIndex > 0) {
        const prevArticle = navigableArticles[currentIndex - 1];
        prevButton.href = `/blogs/eating-guide/${prevArticle.handle}`;
        prevButton.classList.remove("disabled-pill");
        prevButton.removeAttribute("aria-disabled");
      } else {
        prevButton.removeAttribute("href");
        prevButton.classList.add("disabled-pill");
        prevButton.setAttribute("aria-disabled", "true");
      }

      if (currentIndex < navigableArticles.length - 1) {
        const nextArticle = navigableArticles[currentIndex + 1];
        nextButton.href = `/blogs/eating-guide/${nextArticle.handle}`;
        nextButton.classList.remove("disabled-pill");
        nextButton.removeAttribute("aria-disabled");
      } else {
        nextButton.removeAttribute("href");
        nextButton.classList.add("disabled-pill");
        nextButton.setAttribute("aria-disabled", "true");
      }
      navContainer.style.display = "flex";
    }

    setupNavigation();
  }

  function setupBackButtonOnArticlePage() {
    const backButton = document.getElementById("back-to-guide-button");
    if (!backButton) {
      console.warn("No back-to-guide button found.");
      return;
    }
    const baseBlogUrl = backButton.getAttribute("href") || '/blogs/eating-guide';
    backButton.href = baseBlogUrl;
  }

  setupBackButtonOnArticlePage();
  scheduleIdle(() => {
    displayRelatedArticles();
    setupOftenMistakenItems();
    setupNextPrevArticleNavigation();
    hydrateRichTextBlocks();
  });
}

/* Section: Bootstrap */
document.addEventListener("DOMContentLoaded", () => {
  window.eatingGuideObserver = new IntersectionObserver(
    (entries, observerInstance) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const imgToLoad = entry.target;
          if (imgToLoad && imgToLoad.dataset.src) {
            imgToLoad.src = imgToLoad.dataset.src;
            imgToLoad.removeAttribute("data-src");
            imgToLoad.classList.add("visible");
            const card = imgToLoad.closest('.eating-guide-card');
            if (card) {
              card.classList.add("visible");
            }
          }
          observerInstance.unobserve(imgToLoad);
        }
      });
    },
    { threshold: 0.1, rootMargin: "200px" }
  );

  if (document.getElementById("article-grid")) {
    initializeMainGuidePage();
  } else if (document.querySelector(".eating-guide-article .article-content")) {
    initializeSingleArticlePage();
    const mainImg = document.querySelector('.eating-guide-article .lazy-image');
    if (mainImg) {
      window.eatingGuideObserver.observe(mainImg);
    }
  }

  const typeScrollElement = document.querySelector(".eating-guide-type-scroll");
  if (typeScrollElement && typeScrollElement.offsetParent !== null) {
    scheduleIdle(() => {
      typeScrollElement.classList.add("animate-waggle");
    });
  }
});
